import { Notyf } from 'notyf';

document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector<HTMLElement>('.invoices-list-page');
    if (!page) return;

    initSelection();
    initSortHeaders();
    initSearch();
    initLazyStats(page);
    initLazyTypeCounts(page);
    initBulkActions();
});

// ── Selection ─────────────────────────────────────────────────────────────

let selectedIds = new Set<string>();

function initSelection() {
    const checkAll  = document.getElementById('ilp-check-all');
    const tbody     = document.getElementById('ilp-tbody');
    const bulkBar   = document.getElementById('ilp-bulk-bar');
    const bulkCount = document.getElementById('ilp-bulk-count');
    const bulkClear = document.getElementById('ilp-bulk-clear');

    if (!checkAll || !tbody || !bulkBar || !bulkCount || !bulkClear) return;

    tbody.querySelectorAll<HTMLElement>('.ilp-row-check').forEach(box => {
        box.addEventListener('click', () => toggleRow(box));
        box.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleRow(box); }
        });
    });

    checkAll.addEventListener('click', () => {
        const allBoxes = Array.from(tbody.querySelectorAll<HTMLElement>('.ilp-row-check'));
        const allChecked = allBoxes.every(b => b.classList.contains('checked'));
        if (allChecked) {
            allBoxes.forEach(b => { b.classList.remove('checked'); deselectRow(b); });
        } else {
            allBoxes.forEach(b => { b.classList.add('checked'); selectRow(b); });
        }
        updateBulkBar();
        checkAll.classList.toggle('checked', !allChecked);
    });
    checkAll.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); checkAll.click(); }
    });

    bulkClear.addEventListener('click', () => clearSelection());
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape' && selectedIds.size > 0) clearSelection();
    });

    function toggleRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        const id = tr.dataset.id ?? '';
        if (selectedIds.has(id)) {
            selectedIds.delete(id); box.classList.remove('checked'); tr.classList.remove('ilp-tr--selected');
        } else {
            selectedIds.add(id); box.classList.add('checked'); tr.classList.add('ilp-tr--selected');
        }
        updateBulkBar();
    }

    function selectRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        selectedIds.add(tr.dataset.id ?? ''); tr.classList.add('ilp-tr--selected');
    }

    function deselectRow(box: HTMLElement) {
        const tr = box.closest('tr') as HTMLElement | null;
        if (!tr) return;
        selectedIds.delete(tr.dataset.id ?? ''); tr.classList.remove('ilp-tr--selected');
    }

    function updateBulkBar() {
        const n = selectedIds.size;
        bulkBar!.style.display = n > 0 ? 'flex' : 'none';
        const countText = bulkCount!.textContent?.replace(/^\d+\s*/, '') ?? '';
        bulkCount!.textContent = `${n} ${countText}`;
        const allBoxes = Array.from(tbody!.querySelectorAll<HTMLElement>('.ilp-row-check'));
        const allChecked = allBoxes.length > 0 && allBoxes.every(b => b.classList.contains('checked'));
        checkAll!.classList.toggle('checked', allChecked);
    }

    function clearSelection() {
        selectedIds.clear();
        tbody!.querySelectorAll<HTMLElement>('.ilp-row-check').forEach(b => {
            b.classList.remove('checked');
            b.closest('tr')?.classList.remove('ilp-tr--selected');
        });
        checkAll!.classList.remove('checked');
        bulkBar!.style.display = 'none';
    }
}

// ── Bulk actions ──────────────────────────────────────────────────────────

function initBulkActions() {
    const bulkBar = document.getElementById('ilp-bulk-bar');
    if (!bulkBar) return;

    const s     = (window as any).__ilpStrings ?? {};
    const notyf = new Notyf({ duration: 3000 });

    bulkBar.querySelectorAll<HTMLElement>('[data-action]').forEach(btn => {
        btn.addEventListener('click', () => {
            notyf.open({ type: 'warning', message: s.comingSoon ?? 'Coming soon' });
        });
    });
}

// ── Sort headers ──────────────────────────────────────────────────────────

function initSortHeaders() {
    const form      = document.getElementById('ilp-filter-form') as HTMLFormElement | null;
    const sortInput = document.getElementById('ilp-sort-order') as HTMLInputElement | null;
    const pageInput = document.getElementById('ilp-page-input') as HTMLInputElement | null;
    if (!form || !sortInput) return;

    document.querySelectorAll<HTMLElement>('.ilp-th--sortable').forEach(th => {
        th.addEventListener('click', () => {
            const key     = th.dataset.sortKey ?? '';
            const current = sortInput.value;
            const next    = current === key ? `${key}_desc` : key;
            sortInput.value = next;
            if (pageInput) pageInput.value = '0';
            form.submit();
        });
    });
}

// ── Search debounce ───────────────────────────────────────────────────────

function initSearch() {
    const form      = document.getElementById('ilp-filter-form') as HTMLFormElement | null;
    const input     = document.getElementById('ilp-search-input') as HTMLInputElement | null;
    const pageInput = document.getElementById('ilp-page-input') as HTMLInputElement | null;
    if (!form || !input) return;

    let timer: ReturnType<typeof setTimeout>;
    input.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            if (pageInput) pageInput.value = '0';
            form.submit();
        }, 280);
    });
}

// ── Lazy stats ────────────────────────────────────────────────────────────

interface InvoiceListStatsDTO {
    issuedTotal: number;
    issuedCount: number;
    collectedTotal: number;
    collectedPercent: number;
    outstandingTotal: number;
    dueSoonCount: number;
    overdueTotal: number;
    overdueCount: number;
    currency: string;
}

async function fetchJSON<T>(url: string): Promise<T | null> {
    try {
        const r = await fetch(url, { headers: { Accept: 'application/json' } });
        const j = await r.json();
        return j.success ? (j.data as T) : null;
    } catch { return null; }
}

function fmtMoney(n: number, currency: string): string {
    if (n >= 1000000) return `${(n / 1000000).toLocaleString('sr-RS', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M ${currency}`;
    if (n >= 1000)    return `${Math.round(n / 1000)}k ${currency}`;
    return `${n.toLocaleString('sr-RS', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`;
}

function initLazyStats(page: HTMLElement) {
    const url = page.dataset.urlStats ?? '';
    const s   = (window as any).__ilpStrings ?? {};
    loadStats(url, s);
}

async function loadStats(url: string, s: Record<string, string>) {
    const grid = document.getElementById('ilp-stats-grid');
    if (!grid || !url) return;

    const data = await fetchJSON<InvoiceListStatsDTO>(url);
    if (!data) return;

    const now    = new Date();
    const lang   = document.documentElement.lang || 'sr-RS';
    const period = now.toLocaleString(lang, { month: 'long', year: 'numeric' });
    const periodCap = period.charAt(0).toUpperCase() + period.slice(1);

    grid.innerHTML = `
        <div class="ilp-stat-card">
            <div class="ilp-stat-label">
                <div class="ilp-stat-icon" style="background:var(--ilp-accent-light);color:var(--ilp-accent)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18l3-2 3 2 3-2 3 2 3-2 3 2V3l-3 2-3-2-3 2-3-2-3 2-3-2z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="12" y2="16"/></svg>
                </div>
                ${s.statIssued ?? 'Izdato'} · ${periodCap}
            </div>
            <div class="ilp-stat-value">${fmtMoney(data.issuedTotal, data.currency)}</div>
            <div class="ilp-stat-foot">
                <span class="ilp-stat-delta up">
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>
                </span>
                <span>${data.issuedCount} ${s.statIssuedFoot ?? 'faktura'}</span>
            </div>
            <svg class="ilp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 30 L15 24 L30 28 L45 18 L60 22 L75 12 L90 16 L105 6 L120 10 L120 42 L0 42 Z" fill="#F5A623" opacity="0.10"/>
                <path d="M0 30 L15 24 L30 28 L45 18 L60 22 L75 12 L90 16 L105 6 L120 10" stroke="#F5A623" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="ilp-stat-card">
            <div class="ilp-stat-label">
                <div class="ilp-stat-icon" style="background:var(--ilp-success-light);color:var(--ilp-success)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4h7a4 4 0 010 8H6"/><path d="M6 12h8a4 4 0 01-2 7L6 22"/><path d="M3 8h6M3 14h6"/></svg>
                </div>
                ${s.statCollected ?? 'Naplaćeno'}
            </div>
            <div class="ilp-stat-value">${fmtMoney(data.collectedTotal, data.currency)}</div>
            <div class="ilp-stat-foot">
                <span class="ilp-stat-delta up">
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>
                    ${data.collectedPercent}%
                </span>
                <span>${s.statCollectedFoot ?? 'od izdatog'}</span>
            </div>
            <svg class="ilp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 32 L20 26 L40 30 L60 20 L80 14 L100 8 L120 12 L120 42 L0 42 Z" fill="#05A677" opacity="0.10"/>
                <path d="M0 32 L20 26 L40 30 L60 20 L80 14 L100 8 L120 12" stroke="#05A677" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="ilp-stat-card">
            <div class="ilp-stat-label">
                <div class="ilp-stat-icon" style="background:var(--ilp-warning-light);color:var(--ilp-warning)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                </div>
                ${s.statOutstanding ?? 'Neplaćeno'}
            </div>
            <div class="ilp-stat-value">${fmtMoney(data.outstandingTotal, data.currency)}</div>
            <div class="ilp-stat-foot">
                ${data.dueSoonCount > 0
                    ? `<span class="ilp-stat-delta warn"><svg width="9" height="9" viewBox="0 0 10 10" fill="none"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>${data.dueSoonCount}</span>`
                    : ''}
                <span>${s.statOutstandingFoot ?? 'ističe ove sedmice'}</span>
            </div>
            <svg class="ilp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 28 L15 24 L30 30 L45 22 L60 26 L75 18 L90 22 L105 14 L120 18 L120 42 L0 42 Z" fill="#F5A623" opacity="0.10"/>
                <path d="M0 28 L15 24 L30 30 L45 22 L60 26 L75 18 L90 22 L105 14 L120 18" stroke="#F5A623" stroke-width="1.6" fill="none"/>
            </svg>
        </div>
        <div class="ilp-stat-card">
            <div class="ilp-stat-label">
                <div class="ilp-stat-icon" style="background:var(--ilp-danger-light);color:var(--ilp-danger)">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                ${s.statOverdue ?? 'Kasni naplata'}
            </div>
            <div class="ilp-stat-value">${fmtMoney(data.overdueTotal, data.currency)}</div>
            <div class="ilp-stat-foot">
                ${data.overdueCount > 0
                    ? `<span class="ilp-stat-delta down"><svg width="9" height="9" viewBox="0 0 10 10" fill="none" style="transform:rotate(180deg)"><path d="M5 2L8 7H2L5 2Z" fill="currentColor"/></svg>${data.overdueCount}</span>`
                    : ''}
                <span>${s.statOverdueFoot ?? 'faktura van roka'}</span>
            </div>
            <svg class="ilp-stat-spark" width="120" height="42" viewBox="0 0 120 42" fill="none" preserveAspectRatio="none">
                <path d="M0 20 L15 26 L30 22 L45 28 L60 16 L75 20 L90 10 L105 14 L120 8 L120 42 L0 42 Z" fill="#FA5252" opacity="0.10"/>
                <path d="M0 20 L15 26 L30 22 L45 28 L60 16 L75 20 L90 10 L105 14 L120 8" stroke="#FA5252" stroke-width="1.6" fill="none"/>
            </svg>
        </div>`;

    // Remove skeleton class once loaded
    grid.classList.remove('ilp-stats-skeleton');
}

// ── Lazy type counts ──────────────────────────────────────────────────────

interface InvoiceTypeCountsDTO {
    all: number;
    b2c: number;
    b2b: number;
}

function initLazyTypeCounts(page: HTMLElement) {
    const url = page.dataset.urlTypeCounts ?? '';
    if (!url) return;
    loadTypeCounts(url);
}

async function loadTypeCounts(url: string) {
    const data = await fetchJSON<InvoiceTypeCountsDTO>(url);
    if (!data) return;

    document.querySelectorAll<HTMLElement>('.ilp-seg__count[data-type-key]').forEach(el => {
        const key = el.dataset.typeKey ?? '';
        const n   = (data as any)[key] ?? 0;
        el.textContent = n > 0 ? String(n) : '';
    });
}
